/**
 * Board do kanban — /kanbans/:id (Fase 6).
 *
 * Colunas horizontais com cards arrastaveis (HTML5 drag-drop nativo, sem
 * libs externas — KISS). Cada coluna eh um estagio; cards podem ser
 * arrastados entre colunas.
 *
 * Recursos:
 *   - Adicionar cartao inline (livre ou tipado)
 *   - Editar/renomear estagios via modal
 *   - Reordenar estagios via drag (futuro — agora via modal)
 *   - Clique no cartao -> SlideOverPanel com detalhes/edicao/delete
 *   - Atalho "Ver ficha" para cards tipados (link para /pedidos/:id, /orcamentos/:id)
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiExternalLink,
  FiPlus,
  FiRefreshCw,
  FiSettings,
  FiTrash2,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";

import EntityHeader from "../../components/EntityHeader";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import {
  atualizarCartao,
  atualizarEstagio,
  criarCartao,
  criarEstagio,
  deletarCartao,
  deletarEstagio,
  moverCartao,
  obterBoard,
  reordenarEstagios,
} from "../../services/kanbans";
import { listarPedidos } from "../../services/pedidos";
import { listarOrcamentos } from "../../services/orcamentos";
import { PageContainer } from "../../styles/shared";

import {
  BoardColumns,
  BoardScroller,
  Card,
  CartaoMeta,
  CartaoRoot,
  CartaoTitulo,
  ColumnBody,
  ColumnCount,
  ColumnFooter,
  ColumnHeader,
  ColumnRoot,
  DangerButton,
  EmptyMsg,
  ErrorMsg,
  Field,
  Form,
  FormFooter,
  IconButton,
  InlineHint,
  Input,
  Pill,
  PrimaryButton,
  SecondaryButton,
  Select,
  StageEditorRow,
  Textarea,
} from "./styles";

const TONES = [
  { value: "neutral", label: "Neutro" },
  { value: "info", label: "Em progresso (azul)" },
  { value: "success", label: "Concluido (verde)" },
  { value: "warning", label: "Atenção (amarelo)" },
  { value: "danger", label: "Bloqueado (vermelho)" },
];

function Board() {
  const navigate = useNavigate();
  const { kanbanId } = useParams();
  const { request, isMaster, hasAllPermissions } = useAuth();

  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  // Drag state
  const [dragCartaoId, setDragCartaoId] = useState(null);
  const [dragOverEstagioId, setDragOverEstagioId] = useState(null);

  // Modais
  const [cartaoSel, setCartaoSel] = useState(null); // SlideOverPanel
  const [editorEstagios, setEditorEstagios] = useState(false);
  const [novoCartao, setNovoCartao] = useState(null); // {estagio_id, titulo, descricao, entity_id?}

  // Para kanbans tipados: lista de entidades disponiveis pra associar
  const [opcoesEntidade, setOpcoesEntidade] = useState([]);

  const podeEditar = isMaster || hasAllPermissions(["kanban:edit"]);

  // ============== Carrega board ==============

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await obterBoard(request, kanbanId);
      setBoard(data);
      setErro(null);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar kanban.");
    } finally {
      setLoading(false);
    }
  }, [request, kanbanId]);

  useEffect(() => {
    if (kanbanId) carregar();
  }, [kanbanId, carregar]);

  // ============== Carrega opcoes de entidade (pra criar card tipado) ==============

  useEffect(() => {
    if (!board || board.tipo_entidade === "LIVRE") {
      setOpcoesEntidade([]);
      return;
    }
    const lister = board.tipo_entidade === "PEDIDO" ? listarPedidos : listarOrcamentos;
    lister(request, { pageSize: 100 })
      .then((payload) => {
        const items = Array.isArray(payload) ? payload : payload?.results || [];
        setOpcoesEntidade(items);
      })
      .catch(() => setOpcoesEntidade([]));
  }, [board, request]);

  // ============== Drag handlers ==============

  const handleDragStart = (e, cartao) => {
    if (!podeEditar) return;
    setDragCartaoId(cartao.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", cartao.id);
  };

  const handleDragEnd = () => {
    setDragCartaoId(null);
    setDragOverEstagioId(null);
  };

  const handleDragOver = (e, estagioId) => {
    if (!dragCartaoId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverEstagioId !== estagioId) {
      setDragOverEstagioId(estagioId);
    }
  };

  const handleDragLeave = () => {
    setDragOverEstagioId(null);
  };

  const handleDrop = async (e, estagioDestinoId) => {
    e.preventDefault();
    const cartaoId = dragCartaoId || e.dataTransfer.getData("text/plain");
    handleDragEnd();
    if (!cartaoId) return;

    const cartao = board.cartoes.find((c) => c.id === cartaoId);
    if (!cartao || cartao.estagio === estagioDestinoId) return;

    // Otimista: atualiza UI imediato
    setBoard((b) => ({
      ...b,
      cartoes: b.cartoes.map((c) =>
        c.id === cartaoId ? { ...c, estagio: estagioDestinoId } : c
      ),
    }));
    try {
      await moverCartao(request, cartaoId, { estagio_destino: estagioDestinoId });
      // Reload pra ordens consistentes
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro ao mover cartao.");
      await carregar(); // rollback via reload
    }
  };

  // ============== Cartao actions ==============

  const handleCriarCartao = async (e) => {
    e.preventDefault();
    if (!novoCartao) return;
    if (!novoCartao.titulo.trim()) {
      setErro("Título eh obrigatório.");
      return;
    }
    const payload = {
      kanban: board.id,
      estagio: novoCartao.estagio_id,
      titulo: novoCartao.titulo.trim(),
      descricao: novoCartao.descricao || "",
    };
    if (board.tipo_entidade !== "LIVRE") {
      if (!novoCartao.entity_id) {
        setErro("Selecione uma entidade.");
        return;
      }
      payload.entity_type = board.tipo_entidade.toLowerCase();
      payload.entity_id = novoCartao.entity_id;
    }
    try {
      await criarCartao(request, payload);
      setNovoCartao(null);
      setErro(null);
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro ao criar cartao.");
    }
  };

  const handleAtualizarCartao = async (cartaoId, dados) => {
    try {
      await atualizarCartao(request, cartaoId, dados);
      await carregar();
      // Atualiza o selecionado
      setCartaoSel((s) => (s && s.id === cartaoId ? { ...s, ...dados } : s));
    } catch (err) {
      setErro(err?.message || "Erro ao atualizar.");
    }
  };

  const handleDeletarCartao = async (cartaoId) => {
    if (!window.confirm("Apagar este cartao?")) return;
    try {
      await deletarCartao(request, cartaoId);
      setCartaoSel(null);
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro ao apagar.");
    }
  };

  // ============== Render ==============

  if (loading) {
    return (
      <PageContainer>
        <EmptyMsg>Carregando kanban...</EmptyMsg>
      </PageContainer>
    );
  }
  if (erro && !board) {
    return (
      <PageContainer>
        <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>
        <SecondaryButton type="button" onClick={() => navigate("/kanbans")}>
          <FiArrowLeft size={14} /> Voltar
        </SecondaryButton>
      </PageContainer>
    );
  }
  if (!board) return null;

  // Cartoes por estagio
  const cartoesPor = {};
  board.estagios.forEach((e) => {
    cartoesPor[e.id] = [];
  });
  board.cartoes.forEach((c) => {
    const lista = cartoesPor[c.estagio] || [];
    lista.push(c);
    cartoesPor[c.estagio] = lista;
  });
  // Ordena cada coluna
  Object.values(cartoesPor).forEach((lista) =>
    lista.sort((a, b) => a.ordem - b.ordem)
  );

  return (
    <PageContainer>
      <EntityHeader
        title={board.nome}
        subtitle={`${board.cartoes.length} cartao${board.cartoes.length !== 1 ? "es" : ""} em ${board.estagios.length} estagio${board.estagios.length !== 1 ? "s" : ""}`}
        actions={[
          {
            label: "Voltar",
            icon: <FiArrowLeft size={14} />,
            onClick: () => navigate("/kanbans"),
          },
          ...(podeEditar
            ? [{
                label: "Editar estagios",
                icon: <FiSettings size={14} />,
                onClick: () => setEditorEstagios(true),
              }]
            : []),
          {
            label: "Atualizar",
            icon: <FiRefreshCw size={14} />,
            onClick: carregar,
          },
        ]}
      />

      {erro && (
        <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>
      )}

      <BoardScroller>
        <BoardColumns>
          {board.estagios.map((estagio) => (
            <ColumnRoot
              key={estagio.id}
              $dragOver={dragOverEstagioId === estagio.id}
              onDragOver={(e) => handleDragOver(e, estagio.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, estagio.id)}
            >
              <ColumnHeader $tone={estagio.cor_tone}>
                <strong title={estagio.nome}>{estagio.nome}</strong>
                <ColumnCount>{(cartoesPor[estagio.id] || []).length}</ColumnCount>
              </ColumnHeader>
              <ColumnBody>
                {(cartoesPor[estagio.id] || []).length === 0 && (
                  <InlineHint style={{ padding: 8, textAlign: "center" }}>
                    Vazio — arraste cartoes aqui
                  </InlineHint>
                )}
                {(cartoesPor[estagio.id] || []).map((cartao) => (
                  <CartaoRoot
                    key={cartao.id}
                    draggable={podeEditar}
                    onDragStart={(e) => handleDragStart(e, cartao)}
                    onDragEnd={handleDragEnd}
                    $dragging={dragCartaoId === cartao.id}
                    onClick={() => setCartaoSel(cartao)}
                    title="Clique para detalhes; arraste para mover"
                  >
                    <CartaoTitulo>{cartao.titulo}</CartaoTitulo>
                    {cartao.entity_type && (
                      <CartaoMeta>
                        <Pill $tone="info" style={{ fontSize: "0.62rem" }}>
                          {cartao.entity_type}
                        </Pill>
                      </CartaoMeta>
                    )}
                  </CartaoRoot>
                ))}
              </ColumnBody>
              {podeEditar && (
                <ColumnFooter>
                  <SecondaryButton
                    type="button"
                    onClick={() => setNovoCartao({
                      estagio_id: estagio.id,
                      titulo: "",
                      descricao: "",
                      entity_id: "",
                    })}
                    style={{ flex: 1, justifyContent: "center", fontSize: "0.78rem", padding: "5px 10px" }}
                  >
                    <FiPlus size={12} /> Cartao
                  </SecondaryButton>
                </ColumnFooter>
              )}
            </ColumnRoot>
          ))}
        </BoardColumns>
      </BoardScroller>

      {/* ============== SlideOverPanel: detalhe do cartao ============== */}
      <SlideOverPanel
        open={Boolean(cartaoSel)}
        onClose={() => setCartaoSel(null)}
        title={cartaoSel ? "Detalhe do cartao" : ""}
        subtitle={cartaoSel?.kanban_nome}
        width="min(90vw, 480px)"
      >
        {cartaoSel && (
          <CartaoDetalhe
            cartao={cartaoSel}
            podeEditar={podeEditar}
            onAtualizar={(dados) => handleAtualizarCartao(cartaoSel.id, dados)}
            onDeletar={() => handleDeletarCartao(cartaoSel.id)}
            onNavigate={(rota) => {
              setCartaoSel(null);
              navigate(rota);
            }}
          />
        )}
      </SlideOverPanel>

      {/* ============== SlideOverPanel: novo cartao ============== */}
      <SlideOverPanel
        open={Boolean(novoCartao)}
        onClose={() => setNovoCartao(null)}
        title="Novo cartao"
        width="min(90vw, 440px)"
      >
        {novoCartao && (
          <Form onSubmit={handleCriarCartao}>
            {board.tipo_entidade !== "LIVRE" && (
              <Field>
                {board.tipo_entidade === "PEDIDO" ? "Pedido" : "Orçamento"}
                <Select
                  value={novoCartao.entity_id}
                  onChange={(e) => {
                    const ent = opcoesEntidade.find((o) => o.id === e.target.value);
                    setNovoCartao((p) => ({
                      ...p,
                      entity_id: e.target.value,
                      titulo: ent ? `${ent.numero} - ${ent.cliente_nome || ent.cliente?.nome || ""}` : p.titulo,
                    }));
                  }}
                  required
                >
                  <option value="">— Selecione —</option>
                  {opcoesEntidade.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.numero} {o.cliente_nome ? `— ${o.cliente_nome}` : ""}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <Field>
              Titulo
              <Input
                type="text"
                value={novoCartao.titulo}
                onChange={(e) => setNovoCartao((p) => ({ ...p, titulo: e.target.value }))}
                placeholder="Texto curto que aparece no card"
                maxLength={200}
                required
                autoFocus={board.tipo_entidade === "LIVRE"}
              />
            </Field>
            <Field>
              Descricao (opcional)
              <Textarea
                value={novoCartao.descricao}
                onChange={(e) => setNovoCartao((p) => ({ ...p, descricao: e.target.value }))}
                rows={3}
              />
            </Field>
            <FormFooter>
              <SecondaryButton type="button" onClick={() => setNovoCartao(null)}>Cancelar</SecondaryButton>
              <PrimaryButton type="submit">Criar</PrimaryButton>
            </FormFooter>
          </Form>
        )}
      </SlideOverPanel>

      {/* ============== Editor de estagios ============== */}
      <SlideOverPanel
        open={editorEstagios}
        onClose={() => setEditorEstagios(false)}
        title="Editar estagios"
        subtitle="Renomear, adicionar, remover, reordenar"
        width="min(90vw, 520px)"
      >
        {board && (
          <EditorEstagios
            board={board}
            request={request}
            onChange={async () => {
              await carregar();
            }}
          />
        )}
      </SlideOverPanel>
    </PageContainer>
  );
}

// ============================================================
// Subcomponente: detalhe do cartao (edicao inline)
// ============================================================

function CartaoDetalhe({ cartao, podeEditar, onAtualizar, onDeletar, onNavigate }) {
  const [titulo, setTitulo] = useState(cartao.titulo);
  const [descricao, setDescricao] = useState(cartao.descricao || "");
  const [salvando, setSalvando] = useState(false);

  const dirty = titulo !== cartao.titulo || descricao !== (cartao.descricao || "");

  const handleSalvar = async () => {
    setSalvando(true);
    await onAtualizar({ titulo: titulo.trim(), descricao });
    setSalvando(false);
  };

  const linkEntidade = cartao.entity_type === "pedido" && cartao.entity_id
    ? `/pedidos/${cartao.entity_id}`
    : cartao.entity_type === "orcamento" && cartao.entity_id
      ? `/orcamentos/${cartao.entity_id}`
      : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Field>
        Titulo
        <Input
          type="text"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          disabled={!podeEditar}
          maxLength={200}
        />
      </Field>
      <Field>
        Descrição
        <Textarea
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          rows={5}
          disabled={!podeEditar}
        />
      </Field>

      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <Pill $tone="neutral">Estagio: {cartao.estagio_nome}</Pill>
        {cartao.entity_type && (
          <Pill $tone="info">{cartao.entity_type}</Pill>
        )}
      </div>

      {linkEntidade && (
        <SecondaryButton type="button" onClick={() => onNavigate(linkEntidade)}>
          <FiExternalLink size={12} /> Ver ficha completa
        </SecondaryButton>
      )}

      {podeEditar && (
        <div style={{ display: "flex", gap: 8, justifyContent: "space-between", marginTop: 8 }}>
          <DangerButton type="button" onClick={onDeletar}>
            <FiTrash2 size={12} /> Apagar cartao
          </DangerButton>
          <PrimaryButton
            type="button"
            onClick={handleSalvar}
            disabled={salvando || !dirty}
          >
            {salvando ? "Salvando..." : "Salvar"}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Subcomponente: editor de estagios (adicionar/renomear/excluir)
// ============================================================

function EditorEstagios({ board, request, onChange }) {
  const [estagios, setEstagios] = useState(board.estagios);
  const [adicionando, setAdicionando] = useState(false);
  const [novoNome, setNovoNome] = useState("");
  const [novoTone, setNovoTone] = useState("neutral");
  const [erroLocal, setErroLocal] = useState(null);
  const [reordenando, setReordenando] = useState(false);

  useEffect(() => setEstagios(board.estagios), [board.estagios]);

  const handleRenomear = async (estagio, novoValor) => {
    if (novoValor === estagio.nome) return;
    try {
      await atualizarEstagio(request, estagio.id, { nome: novoValor });
      onChange();
    } catch (err) {
      setErroLocal(err?.message || "Erro ao renomear.");
    }
  };

  const handleMudarTone = async (estagio, novoTone) => {
    try {
      await atualizarEstagio(request, estagio.id, { cor_tone: novoTone });
      onChange();
    } catch (err) {
      setErroLocal(err?.message || "Erro ao atualizar cor.");
    }
  };

  const handleExcluir = async (estagio) => {
    if (!window.confirm(`Excluir estagio "${estagio.nome}"? (cartoes precisam ser movidos antes)`)) return;
    try {
      await deletarEstagio(request, estagio.id);
      onChange();
    } catch (err) {
      setErroLocal(err?.message || "Erro ao excluir — cheque se ha cartoes nesse estagio.");
    }
  };

  const handleCriar = async () => {
    if (!novoNome.trim()) return;
    try {
      await criarEstagio(request, {
        kanban: board.id,
        nome: novoNome.trim(),
        cor_tone: novoTone,
      });
      setNovoNome("");
      setNovoTone("neutral");
      setAdicionando(false);
      setErroLocal(null);
      onChange();
    } catch (err) {
      setErroLocal(err?.message || "Erro ao criar estagio.");
    }
  };

  const moveItem = (idx, delta) => {
    const novo = [...estagios];
    const target = idx + delta;
    if (target < 0 || target >= novo.length) return;
    [novo[idx], novo[target]] = [novo[target], novo[idx]];
    setEstagios(novo);
  };

  const salvarReordem = async () => {
    setReordenando(true);
    try {
      await reordenarEstagios(request, board.id, estagios.map((e) => e.id));
      onChange();
      setErroLocal(null);
    } catch (err) {
      setErroLocal(err?.message || "Erro ao reordenar.");
    } finally {
      setReordenando(false);
    }
  };

  const ordemMudou = estagios.some((e, idx) => board.estagios[idx]?.id !== e.id);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {erroLocal && <ErrorMsg><FiAlertCircle size={12} /> {erroLocal}</ErrorMsg>}

      <Card style={{ padding: 8 }}>
        {estagios.map((est, idx) => (
          <StageEditorRow key={est.id}>
            <Input
              type="text"
              defaultValue={est.nome}
              onBlur={(e) => handleRenomear(est, e.target.value)}
              maxLength={80}
              style={{ flex: 1 }}
            />
            <Select
              value={est.cor_tone}
              onChange={(e) => handleMudarTone(est, e.target.value)}
              style={{ width: 140 }}
            >
              {TONES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </Select>
            <IconButton type="button" onClick={() => moveItem(idx, -1)} title="Subir">↑</IconButton>
            <IconButton type="button" onClick={() => moveItem(idx, +1)} title="Descer">↓</IconButton>
            <IconButton type="button" onClick={() => handleExcluir(est)} title="Excluir">
              <FiTrash2 size={11} />
            </IconButton>
          </StageEditorRow>
        ))}
      </Card>

      {ordemMudou && (
        <PrimaryButton type="button" onClick={salvarReordem} disabled={reordenando}>
          {reordenando ? "Aplicando..." : "Salvar nova ordem"}
        </PrimaryButton>
      )}

      {adicionando ? (
        <Card style={{ padding: 12, gap: 6 }}>
          <Field>
            Nome do novo estagio
            <Input
              type="text"
              value={novoNome}
              onChange={(e) => setNovoNome(e.target.value)}
              maxLength={80}
              autoFocus
            />
          </Field>
          <Field>
            Cor
            <Select value={novoTone} onChange={(e) => setNovoTone(e.target.value)}>
              {TONES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </Select>
          </Field>
          <FormFooter>
            <SecondaryButton type="button" onClick={() => setAdicionando(false)}>
              Cancelar
            </SecondaryButton>
            <PrimaryButton type="button" onClick={handleCriar}>
              Adicionar
            </PrimaryButton>
          </FormFooter>
        </Card>
      ) : (
        <SecondaryButton type="button" onClick={() => setAdicionando(true)}>
          <FiPlus size={12} /> Novo estagio
        </SecondaryButton>
      )}
    </div>
  );
}

export default Board;
